import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";

const TRAINING_LOGS_MONTHS_BACK = 6;

export interface UserExportData {
  exportedAt: string;
  uid: string;
  user: Record<string, any> | null;
  private: Record<string, any> | null;
  trainingLogs: Record<string, any>[];
  activityLogs: Record<string, any>[];
  sessions: Record<string, any>[];
  performanceLogs: Record<string, any>[];
}

function mapDocs(docs: { id: string; data: () => Record<string, any> }[]) {
  return docs.map((d) => ({ id: d.id, ...d.data() }));
}

function stripPositionData(activityLogs: Record<string, any>[]) {
  return activityLogs.map(({ positionData, ...rest }) => rest);
}

export async function exportUserData(uid: string): Promise<UserExportData> {
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - TRAINING_LOGS_MONTHS_BACK);

  const trainingLogsQuery = query(
    collection(db, "trainingLogs", uid, "logs"),
    where("timestamp", ">=", cutoffDate.getTime()),
  );

  const [userSnap, privateSnap, trainingLogsSnap, activityLogsSnap, sessionsSnap, performanceLogsSnap] =
    await Promise.all([
      getDoc(doc(db, "users", uid)),
      getDoc(doc(db, "users", uid, "private", "data")),
      getDocs(trainingLogsQuery),
      getDocs(collection(db, "activityLogs", uid, "logs")),
      getDocs(collection(db, "sessions", uid, "sessions")),
      getDocs(collection(db, "performanceLogs", uid, "logs")),
    ]);

  return {
    exportedAt: new Date().toISOString(),
    uid,
    user: userSnap.exists() ? userSnap.data() : null,
    private: privateSnap.exists() ? privateSnap.data() : null,
    trainingLogs: mapDocs(trainingLogsSnap.docs),
    activityLogs: stripPositionData(mapDocs(activityLogsSnap.docs)),
    sessions: mapDocs(sessionsSnap.docs),
    performanceLogs: mapDocs(performanceLogsSnap.docs),
  };
}

export function downloadUserExport(exportData: UserExportData) {
  const blob = new Blob([JSON.stringify(exportData, null, 2)], {
    type: "application/json;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `user-export-${exportData.uid}-${new Date().toISOString().split("T")[0]}.json`,
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
